import { useMemo } from "react";
import { Typography } from "@altimateai/lego";
import { useWorkflowAgents, useWorkflowStatus } from "./useWorkflowQueries";
import { WorkflowAgent } from "./types";
import styles from "./workflow.module.scss";

interface Props {
  onViewConfig: (agentId: string) => void;
  lastRefreshed: Date;
}

function formatRelativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDays = Math.floor(diffHr / 24);
  return `${diffDays}d ago`;
}

function formatModelName(model?: string): string {
  if (!model) return "Not specified";
  // Trim timestamp suffixes like -20250514 for display
  return model.replace(/-\d{8}$/, "");
}

const WorkflowDashboard = ({ onViewConfig, lastRefreshed }: Props) => {
  const { data: apiAgents, isLoading, isError, error } = useWorkflowAgents();
  const agents: WorkflowAgent[] = useMemo(() => apiAgents ?? [], [apiAgents]);

  const { data: statusData } = useWorkflowStatus();

  const stats = useMemo(() => {
    const total = agents.length;
    const enabled = agents.filter(a => a.enabled).length;
    const disabled = total - enabled;
    const online = statusData?.online ?? false;
    return { total, enabled, disabled, online };
  }, [agents, statusData]);

  if (isLoading) {
    return (
      <div className={styles.loadingState}>
        <div className={styles.spinner} />
        <Typography variant="body">Loading agent fleet...</Typography>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles.errorState}>
        <Typography variant="body">
          Failed to load agents: {(error as Error)?.message ?? "Unknown error"}
        </Typography>
      </div>
    );
  }

  return (
    <div>
      {/* Status overview */}
      <div className={styles.fleetOverview}>
        <div className={styles.fleetOverviewLeft}>
          <div className={styles.fleetStatusIndicator}>
            <span
              className={`${styles.statusDot} ${stats.online ? styles.statusDotOnline : styles.statusDotOffline}`}
            />
            <Typography variant="body">
              {stats.online ? "System Online" : "System Offline"}
            </Typography>
          </div>
          <Typography variant="body" className={styles.fleetHeartbeat}>
            Last refreshed: {lastRefreshed.toLocaleTimeString()}
          </Typography>
        </div>
      </div>

      <div className={styles.statsRow}>
        <div className={styles.statCard}>
          <span className={styles.statValue}>{stats.total}</span>
          <span className={styles.statLabel}>Total Agents</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statValue}>{stats.enabled}</span>
          <span className={styles.statLabel}>Enabled</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statValue}>{stats.disabled}</span>
          <span className={styles.statLabel}>Disabled</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statValue}>{stats.online ? "Online" : "Offline"}</span>
          <span className={styles.statLabel}>System Status</span>
        </div>
      </div>

      {/* Empty state */}
      {agents.length === 0 && (
        <div className={styles.fleetEmptyState}>
          <div className={styles.fleetEmptyIcon}>&#x1F916;</div>
          <Typography variant="body" className={styles.fleetEmptyTitle}>
            No agents registered
          </Typography>
          <Typography variant="body" className={styles.fleetEmptyDescription}>
            Your agent fleet is empty. Register an agent through the API or configuration to get
            started. Once registered, agents will appear here with their status, model, and
            capabilities.
          </Typography>
        </div>
      )}

      {/* Agent cards */}
      <div className={styles.agentGrid}>
        {agents.map(agent => (
          <div
            key={agent.id}
            className={styles.agentCard}
            role="button"
            tabIndex={0}
            onClick={() => onViewConfig(agent.id)}
            onKeyDown={e => { if (e.key === "Enter" || e.key === " ") onViewConfig(agent.id); }}
          >
            <div className={styles.agentHeader}>
              <div>
                <h3 className={styles.agentName}>{agent.name}</h3>
                <div className={styles.agentTenant}>{agent.slug}</div>
              </div>
              <span
                className={`${styles.statusBadge} ${styles[agent.enabled ? "online" : "offline"]}`}
              >
                {agent.enabled ? "Enabled" : "Disabled"}
              </span>
            </div>

            {agent.description && <p className={styles.agentDescription}>{agent.description}</p>}

            <dl className={styles.agentMeta}>
              <dt>Model</dt>
              <dd>{formatModelName(agent.model)}</dd>
              {agent.created_at && (
                <>
                  <dt>Registered</dt>
                  <dd>{formatRelativeTime(agent.created_at)}</dd>
                </>
              )}
            </dl>

            {agent.tools && agent.tools.length > 0 && (
              <div className={styles.agentToolsSection}>
                <span className={styles.agentToolsLabel}>Capabilities</span>
                <div className={styles.skillTags}>
                  {agent.tools.map(tool => (
                    <span key={tool} className={styles.skillTag}>
                      {tool}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className={styles.agentActions}>
              <button
                type="button"
                className={styles.configureBtn}
                onClick={e => {
                  e.stopPropagation();
                  onViewConfig(agent.id);
                }}
              >
                Configure
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default WorkflowDashboard;
